import { createSelector } from "@reduxjs/toolkit";

import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";

export const getNmckCreationData = (state) => state.nmck.creationData;
export const getNmckEditingData = (state) => state.nmck.editingData;
export const getNmckFetchStatus = (state) => state.nmck.fetchStatus;
export const getNmckFetchStatusText = (state) => state.nmck.fetchStatusText;
export const getNmckCalcTableData = (state) => state.nmck.calcTableData;
export const getNmckEstmTableData = (state) => state.nmck.estmTableData;
export const getNmckBaseData = (state) => state.nmck.baseData;
export const getNmckReasonsData = (state) => state.nmck.reasonsData;
export const getNmckInflationData = (state) => state.nmck.inflationData;
export const getNmckList = (state) => state.nmck.nmckList;
export const getNmckCalculationWidth = (state) => state.nmck.calculationWidth;
export const getNmckCalcWidthOnTreeTab = (state) => state.nmck.calcWidthOnTreeTab;
export const getNmckTypes = (state) => state.nmck.types;
export const getNmckPeriods = (state) => state.nmck.periods;
export const getNmckWorkTypes = (state) => state.nmck.workTypes;
export const getNmckBooksFetchStatus = (state) => state.nmck.booksFetchStatus;
export const getNmckInitEditingData = (state) => state.nmck.initEditingData;
export const getNmckCreationErrors = (state) => state.nmck.creationErrors;
export const getNmckEditingErrors = (state) => state.nmck.editingErrors;

export const getNmckWorkTypeById = createSelector([getNmckWorkTypes], (types) => {
	return types.reduce((acc, type) => {
		return {
			...acc,
			[type.id]: type,
		};
	}, {});
});

export const getNmckTypeById = createSelector([getNmckTypes], (types) => {
	return types.reduce((acc, type) => {
		return {
			...acc,
			[type.id]: type,
		};
	}, {});
});

export const getNmckPeriodById = createSelector([getNmckPeriods], (periods) => {
	return periods.reduce((acc, period) => {
		return {
			...acc,
			[period.id]: period,
		};
	}, {});
});

dayjs.extend(minMax);
const MONTH_WIDTH = 10;
export const getTimelineData = createSelector([getNmckBaseData, getNmckPeriodById], (baseData, periodById) => {
	const periods = [
		{
			start: dayjs(periodById[baseData.period_building_start_id].date_start),
			end: dayjs(periodById[baseData.period_building_end_id].date_start),
		},
	];

	const dates = [
		dayjs(periodById[baseData.period_price_level_estimate_id].date_start),
		dayjs(periodById[baseData.period_nmck_creation_id].date_start),
		...periods.flatMap((p) => [p.start, p.end]),
	];

	const minDate = dayjs.min(dates).startOf("year");
	const maxDate = dayjs.max(dates).endOf("year").add(1, "month");
	const monthCount = maxDate.diff(minDate, "month");
	const scaleWidth = monthCount * MONTH_WIDTH;

	const getLabel = (idx) => {
		if (idx === 0) {
			return "Уровень цен";
		} else if (idx === 1) {
			return "Дата НМЦК";
		} else if (idx % 2 === 0) {
			return "start";
		}
		return "end";
	};

	const getPosition = (idx) => {
		if (idx === 0) {
			return "top";
		} else if (idx === 1) {
			return "bottom";
		} else if (idx % 2 === 0) {
			return "top";
		}
		return "bottom";
	};

	const points = dates.map((date, idx) => {
		return {
			date,
			label: getLabel(idx),
			position: getPosition(idx),
			offset: date.diff(minDate, "month") * MONTH_WIDTH,
		};
	});

	const ranges = periods.map((p) => {
		const offset = p.start.diff(minDate, "month");
		return {
			offset: offset * MONTH_WIDTH,
			width: (p.end.diff(minDate, "month") - offset) * MONTH_WIDTH,
		};
	});

	const years = new Array(monthCount / 12 + 1).fill(null).map((_, idx) => ({
		year: minDate.year() + idx,
		offset: 12 * MONTH_WIDTH * idx,
	}));

	return {
		points,
		ranges,
		years,
		scaleWidth,
	};
});

export const getNmckCalcTableDataById = createSelector([getNmckCalcTableData], (calcTableData) => {
	return calcTableData.reduce(
		(acc, row) => ({
			...acc,
			[row.nmck_calc_id]: row,
		}),
		{}
	);
});

export const getNmckPeriodLimits = createSelector([getNmckPeriods], (periods) => {
	const sortedPeriods = [...periods].sort((a, b) => {
		if (dayjs(a.date_start).isBefore(dayjs(b.date_start))) {
			return -1;
		}
		return 1;
	});

	const sortedBuildingPeriods = sortedPeriods.filter(({ is_predicted }) => is_predicted);

	return {
		minDate: dayjs(sortedPeriods[0]?.date_start),
		maxDate: dayjs(sortedPeriods.at(-1)?.date_start),
		minBuildingDate: dayjs(sortedBuildingPeriods[0]?.date_start),
		maxBuildingDate: dayjs(sortedBuildingPeriods.at(-1)?.date_start),
	};
});

export const getNmckWorkTypeByAlias = createSelector([getNmckWorkTypes], (workTypes) => {
	return workTypes.reduce(
		(acc, type) => ({
			...acc,
			[type.en_alias]: type,
		}),
		{}
	);
});
